import { useEffect, useMemo, useState } from "react";
import { Form, Col, Row, Modal } from "antd";
import useGet from "../../../global/hooks/useGet";
import usePost from "../../../global/hooks/usePost";
import usePut from "../../../global/hooks/usePut";
import CustomInput from "../../../global/components/inputWidgets/CustomInput";
import CustomSelect from "../../../global/components/inputWidgets/CustomSelect";
import { useNavigate, useParams } from "react-router-dom";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import CommonButton from "../../../global/components/CommonButton";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import {
  CheckCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  UnorderedListOutlined,
  InfoCircleOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import useDelete from "../../../global/hooks/useDelete";
import ConfirmModal from "../../../global/components/ConfirmModal";
import LicenseIcon from "../../../assets/images/vectors/SideBarIcons/LicenseIcon";
import DoorTypeIcon from "../../../assets/images/vectors/DoorTypeIcon";
import OptionCards from "../../../global/components/inputWidgets/OptionCards";
import {
  calculateDoorPrice,
  calculateLicenseTypePrice,
  calculateTotals,
  getItemNoByLabel,
  getPrice,
  modelIconMap,
  orderTypeOptions,
} from "../../../utils/Common";
import LicenseConfirmForm from "./LicenseConfirmModal";

export const LicenseForm = ({
  type,
  isModalForm,
  onCancel,
  setDefaultData,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [form] = Form.useForm();
  const { id } = useParams();
  const navigate = useNavigate();
  const [licenseType, setLicenseType] = useState(0);
  const [licenseOptionsData, setLicenseOptionsData] = useState({});
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);
  const [doorPrice, setDoorPrice] = useState(0);
  const [licenseTypePrice, setLicenseTypePrice] = useState(0);

  const url = id
    ? `${process.env.REACT_APP_API_URL}/api/licenses/${id}/`
    : `${process.env.REACT_APP_API_URL}/api/licenses/`;
  const optionUrl = `${process.env.REACT_APP_API_URL}/api/licenses/options/`;

  const {
    data: optionData,
    loading: optionDataLoading,
    error: optionDataError,
  } = useGet(optionUrl);

  const codesUrl = `${process.env.REACT_APP_API_URL}/api/licensecodes/codes/?limit=100`;
  const {
    data: codesData,
    loading: codesDataLoading,
    error: codesDataError,
  } = useGet(codesUrl);

  const modifiedModels = useMemo(
    () => getModifiedResponse(optionData, "models"),
    [optionData],
  );
  const modifiedModelsOptions = useMemo(
    () =>
      modifiedModels.map((model) => ({
        ...model,
        icon: modelIconMap[model.value],
        searchLabel: model.label,
      })),
    [modifiedModels],
  );

  const modifiedOp = useMemo(
    () =>
      optionData?.data?.options?.map((entry, index) => {
        return {
          value: `${entry?.value}`,
          label: entry.name,
        };
      }),
    [optionData],
  );

  const { data, loading, error, refetch } = useGet(url);

  const {
    data: postData,
    loading: postLoading,
    error: postError,
    postData: postAction,
  } = usePost(url);

  const {
    data: putData,
    loading: putLoading,
    error: putError,
    putData: putAction,
  } = usePut(url);
  const {
    data: deletedData,
    loading: deletedDataLoading,
    error: deletedDataError,
    deleteData,
  } = useDelete(url);

  const handleDoorChange = (e) => {
    const value = e.target.value;
    const doorCount = Number(value);
    const price = calculateDoorPrice(doorCount, licenseType, codesData);
    setDoorPrice(price);
  };
  const handleModelChange = (value) => {
    const price = calculateLicenseTypePrice(
      value,
      form.getFieldValue("LicenseType"),
      codesData,
      modifiedModelsOptions,
    );
    setLicenseTypePrice(price);
  };

  useEffect(() => {
    const currentDoorValue = form.getFieldValue("Type");
    if (currentDoorValue) {
      const price = calculateDoorPrice(
        currentDoorValue,
        licenseType,
        codesData,
      );
      setDoorPrice(price);
    }
  }, [licenseType, codesData]);

  const handleConfirmSubmit = async () => {
    try {
      const optionsArray = Object.entries(licenseOptionsData)
        .filter(([_, val]) => val.type === "toggle" && val.enabled && val.value)
        .map(([_, val]) => Number(val.value));

      // if (form.getFieldValue("Expired")) {
      //   optionsArray.push(64);
      // }

      // Extract field values (quantities) from licenseOptionsData
      const fieldValues = {};
      Object.entries(licenseOptionsData).forEach(([key, val]) => {
        if ((val.type === "input" || val.type === "select") && val.fieldName) {
          // Convert quantity to number for numeric fields
          const quantity = Number(val.quantity);
          fieldValues[val.fieldName] = isNaN(quantity)
            ? val.quantity
            : quantity;
        }
      });

      // Build the final payload
      const payload = {
        LicenseName: pendingFormData.LicenseName,
        LicenseDesc: pendingFormData.LicenseDesc || "",
        Model: Number(pendingFormData.Model),
        Type: Number(pendingFormData.Type),
        LicenseType: pendingFormData.LicenseType ?? 0,
        Options: optionsArray,
        // Add quantities with proper conversion
        Channel: fieldValues.Channel ? Number(fieldValues.Channel) : 0,
        Lockset: fieldValues.Lockset ? Number(fieldValues.Lockset) : 0,
        ContLock: fieldValues.ContLock ? Number(fieldValues.ContLock) : 0,
        Facegate: fieldValues.Facegate ? Number(fieldValues.Facegate) : 0,
        Subnode: fieldValues.Subnode ? Number(fieldValues.Subnode) : 0,
        Intercom: fieldValues.Intercom ? Number(fieldValues.Intercom) : 0,
        Sicucom:
          fieldValues.Sicucom != null && fieldValues.Sicucom !== ""
            ? Number(fieldValues.Sicucom)
            : 0,
        Invitation: fieldValues.Invitation
          ? Number(fieldValues.Invitation) / 100
          : 0,
        Monthly: pendingFormData.Monthly
          ? Number(parseFloat(pendingFormData.Monthly).toFixed(2))
          : pendingFormData.Monthly,
        OneTime: pendingFormData.OneTime
          ? Number(parseFloat(pendingFormData.OneTime).toFixed(2))
          : pendingFormData.OneTime,
      };

      // Check if editing or creating
      if (id) {
        // Edit mode - call PUT API
        await putAction(payload);
      } else {
        // Create mode - call POST API
        await postAction(payload);
      }

      // Close the modal
      setShowSummaryModal(false);
    } catch (error) {
      console.error("Submission error:", error);
      openErrorNotification(
        id
          ? "Failed to update license. Please try again."
          : "Failed to create license. Please try again.",
      );
    }
  };
  const isValidNvrLicenseConfiguration = () => {
    const channel = licenseOptionsData["Channel"];
    const hasMultipleChannels = channel && channel.quantity >= 1;

    if (!hasMultipleChannels) {
      return true; // No validation needed if Channel quantity is 1 or less
    }

    // Check if at least one of the required options is present and enabled
    const requiredOptions = [
      "Vitek NVR",
      "Thumb Drive NVR",
      "Speco NVR",
      "DW Spectrum",
    ];

    const hasRequiredOption = requiredOptions.some((optionName) => {
      const option = licenseOptionsData[optionName];
      return option && option.enabled === true;
    });

    return hasRequiredOption;
  };
  const isValidMobileLicenseConfiguration = () => {
    const mobCred = licenseOptionsData["Mobile Credential"];
    const hasMultipleMobCred = mobCred && mobCred.quantity >= 1;

    if (!hasMultipleMobCred) {
      return true;
    }
    // Check if at least one of the required options is present and enabled
    const requiredOptions = [
      "Sicunet Mobile",
      "Wavelynx Mobile",
      "Farpointe Mobile",
    ];

    const hasRequiredOption = requiredOptions.some((optionName) => {
      const option = licenseOptionsData[optionName];
      return option && option.enabled === true;
    });

    return hasRequiredOption;
  };

  const onFinish = async (values) => {
    try {
      if (id) {
        const optionsArray = Object.entries(licenseOptionsData)
          .filter(
            ([_, val]) => val.type === "toggle" && val.enabled && val.value,
          )
          .map(([_, val]) => Number(val.value));

        // Extract field values from optionsData
        const fieldValues = {};
        Object.entries(licenseOptionsData).forEach(([key, val]) => {
          if (
            (val.type === "input" || val.type === "select") &&
            val.fieldName
          ) {
            fieldValues[val.fieldName] = val.quantity;
          }
        });

        if (!isValidNvrLicenseConfiguration()) {
          return openErrorNotification("At Least one NVR must be selected");
        }
        if (!isValidMobileLicenseConfiguration()) {
          return openErrorNotification(
            "At Least one Mobile Credential must be selected",
          );
        }

        // Merge everything for the modal
        const mergedData = {
          ...values,
          ...fieldValues,
          Options: optionsArray,
          Type: Number(values.Type),
          Model: Number(values.Model),
          LicenseType: values.LicenseType ?? 0,
          Sicucom:
            fieldValues.Sicucom != null && fieldValues.Sicucom !== ""
              ? Number(fieldValues.Sicucom)
              : 0,
          Invitation: fieldValues.Invitation ? fieldValues.Invitation / 100 : 0,
          Monthly: values.Monthly
            ? Number(parseFloat(values.Monthly).toFixed(2))
            : values.Monthly,
          OneTime: values.OneTime
            ? Number(parseFloat(values.OneTime).toFixed(2))
            : values.OneTime,
        };

        // Store merged data and show summary modal
        setPendingFormData(mergedData);
        setShowSummaryModal(true);
      } else {
        if (!isValidNvrLicenseConfiguration()) {
          return openErrorNotification("At Least one NVR must be selected");
        }
        if (!isValidMobileLicenseConfiguration()) {
          return openErrorNotification(
            "At Least one Mobile Credential must be selected",
          );
        }
        const optionsArray = Object.entries(licenseOptionsData)
          .filter(
            ([_, val]) => val.type === "toggle" && val.enabled && val.value,
          )
          .map(([_, val]) => Number(val.value));

        // Extract field values from optionsData
        const fieldValues = {};
        Object.entries(licenseOptionsData).forEach(([key, val]) => {
          if (
            (val.type === "input" || val.type === "select") &&
            val.fieldName
          ) {
            fieldValues[val.fieldName] = val.quantity;
          }
        });

        // Merge everything into one object for the modal
        const mergedData = {
          ...values,
          ...fieldValues,
          Options: optionsArray,
          Type: Number(values.Type),
          Sicucom:
            fieldValues.Sicucom != null && fieldValues.Sicucom !== ""
              ? Number(fieldValues.Sicucom)
              : 0,
          Invitation: fieldValues.Invitation ? fieldValues.Invitation / 100 : 0,
        };

        // Store merged data and show summary modal
        setPendingFormData(mergedData);
        setShowSummaryModal(true);
        // await postAction(modifiedValue);
      }
    } catch (error) {
      openErrorNotification("Something went wrong.");
      console.log(error);
    }
  };

  const onReset = () => {
    // form.resetFields();
    setLicenseType(0);
    isModalForm ? onCancel() : navigate("/license");
  };

  const onDeleteConfirm = async () => {
    try {
      await deleteData();
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
    setShowModal(false);
  };

  // Set default field value for create not a valid undefined field
  useEffect(() => {
    setLicenseType(orderTypeOptions[0].value);
    if (!id) {
      form.setFieldsValue({
        // OemNo: modifiedOems[0]?.value,
        // Product: modifiedProducts[0]?.value,
        Model: modifiedModelsOptions[0]?.value,
        LicenseType: orderTypeOptions[0].value,
      });
    }
  }, [optionData]);

  useEffect(() => {
    if (!id && optionData?.data?.models && codesData?.data) {
      const price = calculateLicenseTypePrice(
        modifiedModelsOptions[0]?.value,
        orderTypeOptions[0].value,
        codesData,
        modifiedModelsOptions,
      );
      setLicenseTypePrice(price);
    }
  }, [optionData, codesData, id]);

  useEffect(() => {
    if (
      !id ||
      loading ||
      !codesData?.data ||
      !modifiedOp ||
      !modifiedModelsOptions?.length
    )
      return;

    const getAccountInfoById = () => {
      try {
        form.resetFields();
        if (data?.data) {
          form.setFieldsValue({
            LicenseName: String(data?.data?.LicenseName),
            LicenseDesc: data?.data?.LicenseDesc
              ? String(data.data.LicenseDesc)
              : "",
            // LicenseType: data.data.LicenseType,
            // Product: data.data.Product,
            // OldModel: data.data.OldModel,
            Model: data.data.Model,
            // OldType: String(data.data.OldType),
            Type: data.data.Type,
            // SiteType: data.data.SiteType,
            // OemNo: data.data.OemNo,
            Options: data.data.Options?.map((item) => String(item)),
            // Camera: String(data.data.Camera),
            Channel: data.data.Channel,
            Lockset: data.data.Lockset,
            ContLock: data.data.ContLock,
            Facegate: data.data.Facegate,
            Subnode: data.data.Subnode,
            // Storage: data.data.Storage,
            Intercom: data.data.Intercom,
            Sicucom:
              data.data.Sicucom != null ? Number(data.data.Sicucom) : null,
            Invitation: data.data.Invitation
              ? data.data.Invitation * 100
              : null,
            LicenseType: 0,
            // Expired: data.data.Options?.includes(64) ?? false,
          });
          const reconstructedOptionsData = {};

          // Handle toggle options (from Options array)
          if (data.data.Options && modifiedOp) {
            data.data.Options.forEach((optionValue) => {
              const option = modifiedOp.find(
                (opt) => opt.value === String(optionValue),
              );
              if (option) {
                const itemNo = getItemNoByLabel(option.label);
                const price = calculateLicenseTypePrice(
                  data.data.Model,
                  0,
                  codesData,
                  modifiedModelsOptions,
                ); // You might need to store licenseType too
                setLicenseTypePrice(price);
                reconstructedOptionsData[option.label] = {
                  type: "toggle",
                  enabled: true,
                  value: option.value,
                  price: getPrice(option.label, codesData, 0, 1, itemNo), // Use the getPrice function from OptionCards
                };
              }
            });
          }

          // Handle input/select options
          const inputFields = [
            { label: "Channel", fieldName: "Channel", type: "input" },
            { label: "Lockset", fieldName: "Lockset", type: "input" },
            { label: "Trilogy Lockset", fieldName: "ContLock", type: "input" },
            { label: "Facegate", fieldName: "Facegate", type: "input" },
            { label: "OSDP Door", fieldName: "Subnode", type: "input" },
            { label: "Intercom", fieldName: "Intercom", type: "input" },
            {
              label: "Directory Extension",
              fieldName: "Sicucom",
              type: "input",
            },
            {
              label: "Mobile Credential",
              fieldName: "Invitation",
              type: "select",
            },
          ];

          inputFields.forEach(({ label, fieldName, type: cardType }) => {
            const value = data.data[fieldName];
            if (value && value > 0) {
              const quantity =
                fieldName === "Invitation" ? value * 100 : value;

              const option = modifiedOp?.find((opt) => opt.label === label);
              const itemNo = getItemNoByLabel(label);
              reconstructedOptionsData[label] = {
                type: cardType,
                quantity: quantity,
                fieldName: fieldName,
                value: option?.value || null,
                price: getPrice(label, codesData, 0, quantity, itemNo),
              };
            }
          });

          setLicenseOptionsData(reconstructedOptionsData);

          // Also calculate and set the prices
          const doorPrice = calculateDoorPrice(
            data.data.Type,
            data.data.LicenseType || 0,
            codesData,
          );
          setDoorPrice(doorPrice);

          const licensePrice = calculateLicenseTypePrice(
            data.data.Model,
            0,
            codesData,
            modifiedModelsOptions,
          );
          setLicenseTypePrice(licensePrice);
        } else {
          console.warn("No data found for the given ID");
        }
      } catch (error) {
        if (!loading) {
          console.log({ error });
          openErrorNotification("Error fetching data!");
        }
      }
    };

    getAccountInfoById();
  }, [id, data, loading, codesData, modifiedOp, modifiedModelsOptions]);

  //toat for create
  useEffect(() => {
    if (postError) {
      openErrorNotification(
        postError?.response?.data?.message || "Something went wrong",
      );
    } else if (!postLoading && !postError && postData) {
      openConfirmNotification("Success");
      setDefaultData &&
        setDefaultData({
          LicenseNo: postData?.data?.LicenseNo,
        });
      setTimeout(() => {
        if (isModalForm) {
          onCancel();
        } else {
          setLicenseType(0);
          navigate(-1);
        }
      }, 200);
    }
  }, [postError, postData, postLoading]);

  //toat for update
  useEffect(() => {
    if (putError) {
      openErrorNotification(
        putError?.response?.data?.message || "Something went wrong",
      );
    } else if (!putLoading && !putError && putData) {
      openConfirmNotification("Success");
      setDefaultData &&
        setDefaultData({
          LicenseNo: postData?.data?.LicenseNo,
        });
      refetch();
      setTimeout(() => {
        if (isModalForm) {
          onCancel();
        } else {
          setLicenseType(0);
          navigate(-1);
        }
      }, 200);
    }
  }, [putData, putLoading, putError]);

  useEffect(() => {
    if (deletedData && !deletedDataError) {
      openConfirmNotification("Success");
      // refetch();
      navigate("/license");
    } else if (deletedDataError) {
      openErrorNotification(
        deletedDataError?.response?.data?.message || "Something went wrong",
      );
    } else {
      return;
    }
  }, [deletedData, deletedDataError]);

  const totalPrice = calculateTotals(
    licenseOptionsData,
    licenseTypePrice,
    doorPrice,
  );

  const breadcrumbItems = [
    {
      label: "License",
      href: "/license",
    },
    {
      label: id ? (type === "info" ? "Information" : "Edit") : "Add",
      href: id
        ? type === "info"
          ? `/license/info/${id}`
          : `/license/edit/${id}`
        : "/license/add",
    },
  ];

  const buttonsData =
    type === "info"
      ? [
          {
            name: "LicenseType",
            label: "LicenseType",
            radioOptions: orderTypeOptions,
            toggle: true,
            onChange: (value) => {
              setLicenseType(value);
              const price = calculateLicenseTypePrice(
                form.getFieldValue("Model"),
                value,
                codesData,
                modifiedModelsOptions,
              );
              setLicenseTypePrice(price);
            },
          },
          {
            label: "Total Price: ",
            renderLabel: true,
            value: `${"$ " + totalPrice}`,
          },
          {
            label: "Edit",
            icon: <EditOutlined />,
            onClick: () => {
              navigate(`/license/edit/${id}`);
            },
          },
          {
            label: "Delete",
            icon: <DeleteOutlined />,
            onClick: () => {
              setShowModal(true);
            },
          },
          {
            label: "List",
            icon: <UnorderedListOutlined />,
            onClick: () => {
              navigate(-1);
            },
          },
        ]
      : [
          {
            name: "LicenseType",
            label: "LicenseType",
            radioOptions: orderTypeOptions,
            toggle: true,
            onChange: (value) => {
              setLicenseType(value);
              const price = calculateLicenseTypePrice(
                form.getFieldValue("Model"),
                value,
                codesData,
                modifiedModelsOptions,
              );
              setLicenseTypePrice(price);
            },
          },
          {
            label: "Total Price: ",
            renderLabel: true,
            value: `${"$ " + totalPrice}`,
          },
          {
            label: "Apply",
            icon: <CheckCircleOutlined />,
            onClick: () => form.submit(),
          },
          {
            label: "Cancel",
            icon: <CloseOutlined />,
            onClick: onReset,
          },
        ];

  return (
    <>
      <Form
        validateTrigger={"onChange"}
        form={form}
        onFinish={onFinish}
        scrollToFirstError
        layout={"vertical"}
        autoComplete={"off"}
      >
        <div>
          <div className="pb-4 sticky -mt-4 pt-[6px] top-[55px] z-10 bg-[#f4f5f9]">
            <CommonBreadcrumb breadcrumbItems={breadcrumbItems}>
              <CommonButton buttonsData={buttonsData} />
            </CommonBreadcrumb>
          </div>
          <>
            <div className="border bg-white rounded-[4px] overflow-hidden">
              <div className="flex items-center bg-gray-200 p-2.5">
                <LicenseIcon className="text-[18px] text-black " />
                <span className="text-[.935rem] text-black font-['Poppins'] ml-2">
                  License
                </span>
              </div>
              <div className="px-2 pt-1 pb-3">
                <Row gutter={[16, 8]}>
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomInput
                      label="Name"
                      name="LicenseName"
                      disabled={type === "info"}
                      required
                      loading={loading}
                    />
                  </Col>
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomInput
                      label="Description"
                      name="LicenseDesc"
                      disabled={type === "info"}
                      loading={loading}
                    />
                  </Col>
                </Row>
              </div>
            </div>
            <div className="mt-3 border bg-white rounded-[4px] overflow-hidden">
              <div className="bg-gray-200 p-2.5 flex items-center">
                <InfoCircleOutlined className="text-[18px] text-black " />
                <span className="text-[.935rem] text-black font-['Poppins'] ml-2">
                  License Information
                </span>
              </div>
              <div>
                {/* {isInitial ? ( */}
                <div className="px-2 pt-1 pb-3">
                  <Row gutter={[16, 8]}>
                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                      <CustomSelect
                        label="Model"
                        name="Model"
                        options={modifiedModelsOptions}
                        placeholder="Please select"
                        disabled={type === "info"}
                        required
                        loading={loading}
                        onChange={handleModelChange}
                        showIcons
                        postfix={
                          <span className="flex items-center">
                            <span className="h-7 border-l border-gray-300 mr-2"></span>
                            <span className="text-xs font-medium text-gray-500 mr-1">
                              $
                            </span>
                            <span className="text-xs font-medium text-gray-800">
                              {licenseTypePrice.toFixed(2)}
                            </span>
                          </span>
                        }
                      />
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                      <CustomInput
                        label="Door"
                        name="Type"
                        disabled={type === "info"}
                        type="number"
                        required
                        prefix={<DoorTypeIcon />}
                        loading={loading}
                        onChange={handleDoorChange}
                        rules={[
                          {
                            required: true,
                            message: "Door is required",
                          },
                          {
                            validator: (_, value) => {
                              if (value && value > 65535) {
                                return Promise.reject(
                                  new Error(
                                    "Door must be less than or equal to 65535!",
                                  ),
                                );
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                        postfix={
                          <span className="flex items-center">
                            <span className="h-7 border-l border-gray-300 mr-2"></span>
                            <span className="text-xs font-medium text-gray-500 mr-1">
                              $
                            </span>
                            <span className="text-xs font-medium text-gray-800">
                              {doorPrice.toFixed(2)}
                            </span>
                          </span>
                        }
                      />
                    </Col>
                  </Row>
                </div>
              </div>
            </div>
            <div className="mt-3 border bg-white rounded-[4px] overflow-hidden ">
              <div className="bg-gray-200 p-2.5 flex items-center">
                <SettingOutlined className="text-[18px] text-black " />
                <span className="text-[.935rem] text-black font-['Poppins'] ml-2">
                  License Options
                </span>
              </div>
              <div
                className={`px-2 py-3 grid gap-4 ${
                  isModalForm ? " min-[500px]:grid-cols-2 grid-cols-1" : ""
                }`}
              >
                <OptionCards
                  name="Options"
                  options={modifiedOp}
                  disabled={type === "info"}
                  loading={loading || codesDataLoading}
                  codesData={codesData}
                  type={licenseType}
                  value={licenseOptionsData}
                  onChange={setLicenseOptionsData}
                />
              </div>
            </div>
          </>
        </div>
      </Form>
      <Modal
        open={showSummaryModal}
        onCancel={() => setShowSummaryModal(false)}
        closeIcon={false}
        footer={null}
        width={520}
        centered
        motion={false}
        destroyOnClose={true}
      >
        <LicenseConfirmForm
          onConfirm={handleConfirmSubmit}
          onCancel={() => setShowSummaryModal(false)}
          mergedData={pendingFormData}
          optionsData={licenseOptionsData}
          licenseType={licenseType}
          modelsOptions={modifiedModelsOptions}
          setLicenseType={setLicenseType}
          licenseTypePrice={licenseTypePrice}
          doorPrice={doorPrice}
          form={form}
          loading={postLoading || putLoading}
        />
      </Modal>

      <ConfirmModal
        showModal={showModal}
        setShowModal={setShowModal}
        onDeleteConfirm={onDeleteConfirm}
      />
    </>
  );
};
